import React, { useEffect, useState, useMemo } from 'react';
import type { SortOrder } from 'antd/es/table/interface';
import { Button, message, Modal, Row, Space, Spin, Tooltip, Card, Select, Input } from 'antd';
import {
  DeleteOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  CarOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import to from 'await-to-js';
import { useHistory } from 'react-router-dom';
import PageLayout from '../../SharedComponents/PageLayout';
import AntEditingTable from '../../SharedComponents/RSEditableTable/AntEditingTable';
import {
  clearDraftOperations,
  fetchGeoCodingForDraftOperations,
  getDraftOperations,
  getDraftOperationSummary,
  getOperationSubmitLoading,
  isGeoCodingFetching,
  removeDraftOperation,
  submitDraftOperations,
  updateDraftOperation,
  getOptimizationProgress,
} from '../../store/operations.store';
import { showAddDraftOperationModal, showMapView, showSelectVehicleModal } from '../../store/app-ui.store';
import AddressPicker from '../../SharedComponents/Forms/AddressPicker';
import { LocationSearchById, LocationSearchByName } from '../../SharedComponents/Forms/LocationSearch';
import {
  clearSelectedDriversAndVehicles,
  fetchMinimalVehicle,
  getSelectedVehicles,
  getSelectedVehiclesCapacity,
  setVehicleSelectionMode,
} from '../../store/drivers.store';
import { fetchMinimalLocations } from '../../store/location.store';
import MapViewAndErrorTrigger from '../../SharedComponents/MapViewAndErrorTrigger';
import RouteSelectionCard from './RouteSelectionCard';
import type { AddressType } from './LocationSelector';
import type { DetermineOptTypeParams } from '../../../src/Types/routeData';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { LocationItem } from '../../SharedComponents/Forms/ItemsEditor';
import ItemsEditorModal from '../../SharedComponents/Forms/ItemsEditorModal';

const OperationEditingPage = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [startRow, setStartRow] = useState<string | AddressType | undefined>();
  const [endRow, setEndRow] = useState<string | AddressType | undefined>();
  const [singleLocation, setSingleLocation] = useState<string | AddressType | undefined>();
  const [routeType, setRouteType] = useState<'single' | 'multi'>('single');
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [priorityStops, setPriorityStops] = useState<number | undefined>();
  const geoCodeLoading = useAppSelector(isGeoCodingFetching);
  const submitLoading = useAppSelector(getOperationSubmitLoading);
  const data = useAppSelector(getDraftOperations);
  const optimizationProgress = useAppSelector(getOptimizationProgress);

  const maxPriorityInData = React.useMemo(() => {
    if (data.length === 0) return 0;
    return data.reduce((max, item) => {
      const p = Number(item.priority);
      return !Number.isNaN(p) && p > max ? p : max;
    }, 0);
  }, [data]);

  const getNormalizedValue = (val: any): number => {
    if (typeof val === 'number') return val;
    if (val !== undefined) return Number(val);
    return 0;
  };

  React.useEffect(() => {
    if (priorityStops === undefined || priorityStops < maxPriorityInData) {
      setPriorityStops(maxPriorityInData);
    }
  }, [maxPriorityInData, priorityStops]);

  useEffect(() => {
    dispatch(fetchGeoCodingForDraftOperations(0));
    dispatch(fetchMinimalLocations(0));
    dispatch(fetchMinimalVehicle(0));
  }, []);

  useEffect(() => {
    if (routeType === 'multi') {
      data.forEach((row) => {
        if (row.priority !== 0) {
          dispatch(updateDraftOperation({ index: row.key, key: 'priority', data: 0 }));
        }
      });
      setPriorityStops(0);
    }
  }, [routeType, data, dispatch]);

  const updateLocations = (
    key: number,
    lng: number | null,
    lat: number | null,
    locationId: string,
    name: string,
    address: string,
    clearErrors?: boolean,
    waiting_time?: number | null,
    contact_no?: string,
  ) => {
    dispatch(updateDraftOperation({ index: key, key: 'location', data: { lng: Number(lng), lat: Number(lat) } }));
    dispatch(updateDraftOperation({ index: key, key: 'address', data: address }));
    dispatch(updateDraftOperation({ index: key, key: 'locationId', data: locationId }));
    dispatch(updateDraftOperation({ index: key, key: 'name', data: name, clearErrors }));
    dispatch(updateDraftOperation({ index: key, key: 'waiting_time', data: waiting_time }));
    dispatch(updateDraftOperation({ index: key, key: 'contact_no', data: contact_no }));
  };

  const columns = [
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: '24%',
      isEditable: true,
      editingElement: (value: any, rowData: any) => (
        <AddressPicker
          onChange={({ lng, lat, address, waiting_time, contact_no }) => {
            updateLocations(rowData.key, lng, lat, 'N/A', 'N/A', address, true, waiting_time, contact_no);
          }}
          value={value}
        />
      ),
    },
    {
      title: 'Location Id',
      dataIndex: 'locationId',
      key: 'locationId',
      width: '13%',
      isEditable: true,
      editingElement: (value: any, rowData: any) => (
        <LocationSearchById
          loading={false}
          value={value}
          onChange={(
            locationId: string,
            locationName: string,
            address: string,
            location: any,
            waiting_time: number | null,
            contact_no?: string,
          ) => {
            updateLocations(
              rowData.key,
              location.lng,
              location.lat,
              locationId,
              locationName,
              address,
              true,
              waiting_time,
              contact_no,
            );
          }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      isEditable: true,
      editingElement: (value: any, rowData: any) => (
        <LocationSearchByName
          loading={false}
          value={value}
          onChange={(
            locationId: string,
            locationName: string,
            address: string,
            location: any,
            waiting_time: number | null,
            contact_no?: string,
          ) => {
            updateLocations(
              rowData.key,
              location.lng,
              location.lat,
              locationId,
              locationName,
              address,
              true,
              waiting_time,
              contact_no,
            );
          }}
        />
      ),
      width: '13%',
    },
    {
      title: 'Lat',
      key: 'latitude',
      width: '9%',
      render: (text: any, record: any) => record.location?.lat ?? '',
    },
    {
      title: 'Lng',
      key: 'longitude',
      width: '9%',
      render: (text: any, record: any) => record.location?.lng ?? '',
    },
    {
      title: 'Postal Code',
      key: 'postalCode',
      dataIndex: 'postalCode',
      width: '11%',
      isEditable: true,
      editingElement: (value: any, rowData: any) => (
        <Input
          value={value}
          onChange={(e) =>
            dispatch(updateDraftOperation({ index: rowData.key, key: 'postalCode', data: e.target.value }))
          }
        />
      ),
    },
    {
      title: 'Contact Number',
      key: 'contact_no',
      dataIndex: 'contact_no',
      width: '13%',
      isEditable: true,
      editingElement: (value: any, rowData: any) => (
        <Input
          value={value}
          placeholder="e.g. 94771234567"
          onChange={(e) =>
            dispatch(updateDraftOperation({ index: rowData.key, key: 'contact_no', data: e.target.value }))
          }
        />
      ),
    },
    {
      title: 'Priority',
      key: 'priority',
      dataIndex: 'priority',
      width: '12%',
      isEditable: true,
      editingElement: (value: any, row: any) => {
        const locationCount = data.length;
        const maxPriority = Math.min(priorityStops && priorityStops > 0 ? priorityStops : 0, locationCount);
        const usedPriorities = data
          .filter((r) => r.key !== row.key && r.priority !== undefined && Number(r.priority) !== 0)
          .map((r) => Number(r.priority));
        return (
          <Select
            value={getNormalizedValue(value)}
            className="w-100"
            onChange={(newPriority) => {
              dispatch(updateDraftOperation({ index: row.key, key: 'priority', data: newPriority }));
            }}
          >
            {Array.from({ length: maxPriority + 1 }, (_, i) => (
              <Select.Option key={i} value={i} disabled={usedPriorities.includes(i) && i !== 0}>
                {i}
              </Select.Option>
            ))}
          </Select>
        );
      },
      render: (value: any) => (routeType === 'multi' ? 0 : value ?? 0),
      sorter: (a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
    },
    {
      title: 'Value',
      key: 'value',
      dataIndex: 'value',
      width: '11%',
      isEditable: true,
      onValueChange: (index: string, event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(updateDraftOperation({ index, key: 'value', data: event.target.value }));
      },
      sorter: (a: any, b: any) => (Number(a.value) || 0) - (Number(b.value) || 0),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
    },
    {
      title: 'Waiting Time (min)',
      key: 'waiting_time',
      dataIndex: 'waiting_time',
      width: '13%',
      isEditable: true,
      onValueChange: (index: string, event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(updateDraftOperation({ index, key: 'waiting_time', data: event.target.value }));
      },
    },
    {
      title: 'Items',
      key: 'items',
      dataIndex: 'items',
      width: '16%',
      isEditable: true,
      render: (items: LocationItem[]) => {
        if (!items || items.length === 0) {
          return <span style={{ color: '#aaa' }}>No items</span>;
        }
        return (
          <div>
            {items.map((item) => (
              <div key={`${item.item_name}-${item.operation}-${item.capacity}`}>
                <span style={{ color: item.operation === 'pickup' ? '#52c41a' : '#ff4d4f' }}>
                  {item.operation === 'pickup' ? '↑' : '↓'}
                </span>{' '}
                {item.item_name} <span style={{ fontWeight: 600 }}>({item.capacity})</span>
              </div>
            ))}
          </div>
        );
      },
      editingElement: (value: any, rowData: any) => (
        <ItemsEditorModal
          value={value ?? []}
          onChange={(items) => dispatch(updateDraftOperation({ index: rowData.key, key: 'items', data: items }))}
        />
      ),
    },
    {
      title: '',
      key: 'location',
      dataIndex: 'location',
      width: 50,
      className: 'location-btn',
      render: (rowData: any, rowValues: any) => <MapViewAndErrorTrigger data={rowData} rowValues={rowValues} />,
    },
  ];

  const totalWaitingTime = useMemo(() => {
    const totalMinutes = data.reduce((sum, item) => sum + (Number(item.waiting_time) || 0), 0);
    if (totalMinutes === 0) return '0 min';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} hr`;
    return `${hours} hr ${minutes} min`;
  }, [data]);

  const summary = useAppSelector(getDraftOperationSummary);
  const selectedVehicleCap = useAppSelector(getSelectedVehiclesCapacity);
  const selectedVehicles = useAppSelector(getSelectedVehicles);
  const hasLocations = data.length > 0;

  useEffect(() => {
    localStorage.setItem('draft-operations', JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    if (summary.errors === 0) {
      setShowErrorsOnly(false);
    }
  }, [summary.errors]);

  const openAddDraftOperation = () => {
    dispatch(showAddDraftOperationModal(true));
  };

  const openViewAllLocationMap = () => {
    dispatch(
      showMapView({
        show: true,
        locations: data.map((a) => a.location).filter((a) => a && a.lat && a.lng),
      }),
    );
  };
  const openSelectVehicleModal = (type: 'single' | 'multi') => {
    dispatch(setVehicleSelectionMode(type));
    dispatch(showSelectVehicleModal(true));
  };

  const submitLocations = async () => {
    try {
      const optimizationType = determineOptimizationType({
        routeType,
        startRow,
        endRow,
        selectedVehicles,
        singleLocation,
      });
      const submitArgs = { startRow, endRow, singleLocation, routeType, optimizationType };
      const [err] = await to(dispatch(submitDraftOperations(submitArgs)).unwrap());
      if (err) {
        return Modal.error({ title: 'Failed to add operations', content: err.message });
      }
      message.success('Validation Success');
      return history.push('/operations/edit-operation-with-grouping');
    } catch (error) {
      return Modal.error({ title: 'Unexpected Error', content: (error as Error).message || String(error) });
    }
  };

  function determineOptimizationType(params: DetermineOptTypeParams): string {
    const { routeType: rType, startRow: sRow, endRow: eRow, singleLocation: singleLoc } = params;

    if (rType === 'multi') {
      const multiCircular =
        Boolean(singleLoc) ||
        (typeof sRow === 'string' && typeof eRow === 'string' && sRow === eRow) ||
        (typeof sRow === 'object' && typeof eRow === 'object' && sRow && eRow && sRow?.address === eRow?.address);
      return multiCircular ? ':circular' : ':straight';
    }

    if (rType === 'single') {
      const anyPriority = data.some((op) => (op.priority ?? 0) > 0);
      const circular =
        Boolean(singleLoc) ||
        (typeof sRow === 'string' && typeof eRow === 'string' && sRow === eRow) ||
        (typeof sRow === 'object' && typeof eRow === 'object' && sRow?.address === eRow?.address);
      if (anyPriority && circular) {
        return 'single:circular:priority';
      }
      if (anyPriority) {
        return 'single:straight:priority';
      }
      if (circular) {
        return ':circular';
      }
      return ':straight';
    }
    return ':straight';
  }

  const resolvedType = determineOptimizationType({
    routeType,
    startRow,
    endRow,
    selectedVehicles,
    singleLocation,
  });

  const isSameLocationCheck = (operation: any, loc: any): boolean => {
    if (!loc || !operation) {
      return false;
    }
    if (typeof loc === 'string') {
      return operation.address === loc;
    }
    return (
      operation.address === loc.address &&
      Number(operation.location?.lng ?? operation.longitude) === Number(loc.lng) &&
      Number(operation.location?.lat ?? operation.latitude) === Number(loc.lat)
    );
  };

  const discardEditing = () => {
    Modal.warn({
      title: 'Discard Changes?',
      content: 'Are you sure you want to discard the changes?',
      onOk: () => {
        localStorage.removeItem('draft-operations');
        dispatch(clearDraftOperations());
        dispatch(clearSelectedDriversAndVehicles());
        history.goBack();
      },
      okCancel: true,
    });
  };
  const locationsExcludingPriority = useMemo(
    () =>
      data
        .filter(
          (item) =>
            item.address &&
            item.name &&
            item.location?.lat &&
            item.location?.lng &&
            (Number(item.priority) === 0 || item.priority === undefined || item.priority === null),
        )
        .reduce((acc, item) => {
          const alreadyExists = acc.some((existing) => existing.key === item.address);
          if (!alreadyExists) {
            acc.push({
              key: item.address,
              name: item.address,
            });
          }
          return acc;
        }, [] as { key: string; name: string }[]),
    [data],
  );

  const getSpinTip = () => {
    if (optimizationProgress !== null) return `Optimizing... ${optimizationProgress}%`;
    if (submitLoading) return 'Please wait, we are processing...';
    return 'Geo Codes Loading';
  };

  const renderSubmitButton = () => {
    let tooltip = '';
    if (summary.errors > 0) {
      tooltip = 'You have to clear all the error rows';
    } else if (Object.keys(selectedVehicles).length === 0) {
      tooltip = 'You have to select at least one vehicle';
    } else if (!startRow && !endRow && !singleLocation) {
      tooltip = 'Please select a location';
    } else if (startRow && !endRow) {
      tooltip = 'Please select an end location';
    } else if (!startRow && endRow) {
      tooltip = 'Please select a start location';
    } else if (resolvedType === ':circular' || resolvedType === 'single:circular:priority') {
      const depotSource = singleLocation ?? startRow;
      const depotLocation = data.find((op) =>
        typeof depotSource === 'string' ? op.address === depotSource : isSameLocationCheck(op, depotSource),
      );
      const depotHasItems = (depotLocation?.items ?? []).length > 0;
      if (depotHasItems) {
        tooltip = 'The depot (start/end) location must not have items assigned for circular routes';
      }
    }
    return (
      <Row>
        <Space>
          {tooltip ? (
            <Tooltip title={tooltip}>
              <Button type="primary" block disabled>
                Submit
              </Button>
            </Tooltip>
          ) : (
            <Button type="primary" block onClick={submitLocations}>
              Submit
            </Button>
          )}
        </Space>
      </Row>
    );
  };
  return (
    <PageLayout
      title="Update Draft Operation"
      actions={[
        <Button key="2" onClick={discardEditing} danger>
          Discard
        </Button>,
        <Button key="1" onClick={() => history.goBack()}>
          Back
        </Button>,
      ]}
    >
      <Spin spinning={geoCodeLoading || submitLoading} tip={getSpinTip()}>
        <div className="ops-metrics-grid">
          {[
            { label: 'Total Locations', value: summary.locations, icon: <EnvironmentOutlined /> },
            {
              label: 'Total Route Load',
              value: data.reduce(
                (sum, d) => sum + (d.items ?? []).reduce((s: number, item: any) => s + Number(item.capacity ?? 0), 0),
                0,
              ),
              icon: <AppstoreOutlined />,
            },
            { label: 'Vehicle Capacity', value: selectedVehicleCap, icon: <CarOutlined /> },
            {
              label: 'Total Value',
              value: data.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
              icon: <DollarOutlined />,
            },
            { label: 'Waiting Time', value: totalWaitingTime, icon: <ClockCircleOutlined /> },
          ].map(({ label, value, icon }) => (
            <Card key={label} bodyStyle={{ padding: '10px 12px' }}>
              <div className="ops-metrics-grid__header">
                <span className="ops-metrics-grid__label">{label}</span>
                <span className="ops-metrics-grid__icon">{icon}</span>
              </div>
              <div className="ops-metrics-grid__value">{value}</div>
            </Card>
          ))}
        </div>
        <Row justify="center" className="mg-t-15 mg-b-20">
          <Space size="middle" wrap>
            <Button
              onClick={() => setShowErrorsOnly(!showErrorsOnly)}
              disabled={!hasLocations || summary.errors === 0}
              icon={<ExclamationCircleOutlined />}
              className="min-w-180 fw-600"
            >
              {showErrorsOnly ? 'Show All Rows' : 'Show only Error Rows'}
            </Button>
            <Button
              onClick={() => openSelectVehicleModal(routeType)}
              disabled={!hasLocations}
              icon={<CarOutlined />}
              className="min-w-180 fw-600"
            >
              Assign Vehicles
            </Button>
            <Button
              onClick={openViewAllLocationMap}
              disabled={!hasLocations}
              icon={<EnvironmentOutlined />}
              className="min-w-180 fw-600"
            >
              Show All Locations
            </Button>
            <Button onClick={openAddDraftOperation} icon={<PlusOutlined />} className="min-w-180 fw-600">
              Add Operation
            </Button>
          </Space>
        </Row>
        <Card title="Route and Location Management" className="mg-t-15 mg-b-25">
          <RouteSelectionCard
            key={locationsExcludingPriority.map((l) => l.key).join(',')}
            locations={locationsExcludingPriority}
            onRouteTypeChange={setRouteType}
            onStartChange={(address) => setStartRow(address ?? undefined)}
            onEndChange={(address) => setEndRow(address ?? undefined)}
            onSingleChange={(address) => setSingleLocation(address ?? undefined)}
          />
          <div className="mg-t-20 mg-b-25">
            <span id="priorityLabel" className="fw-600 mg-r-10">
              Number of Priority Stops:
            </span>
            <Input
              aria-labelledby="priorityLabel"
              type="number"
              min={1}
              max={data.length}
              value={priorityStops}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setPriorityStops(Number.isNaN(v) ? undefined : v);
              }}
              className="w-120 mg-r-15"
              placeholder={`1 - ${data.length}`}
              disabled={routeType === 'multi'}
            />
          </div>
          <AntEditingTable
            className="mg-t-20 mg-t-10"
            extraButtons={(key: string) => (
              <Button
                className="mg-l-8"
                icon={<DeleteOutlined />}
                danger
                onClick={() => {
                  if (key === startRow) {
                    setStartRow('');
                  }
                  dispatch(removeDraftOperation(key));
                }}
              />
            )}
            columns={columns}
            dataSource={showErrorsOnly ? data.filter((a) => a.error) : data}
            scroll={{ y: 480 }}
            styles={{}}
          />
          <div className="mg-t-25">{renderSubmitButton()}</div>
        </Card>
      </Spin>
    </PageLayout>
  );
};

export default OperationEditingPage;
